<template>
  <div class="discover-page">
    <AppTopHeader />

    <main class="discover-main">
      <header class="discover-hero">
        <h1 class="discover-title">{{ $t('discover.hero.title') }}</h1>
        <p class="discover-lead">
          {{ $t('discover.hero.subtitle') }}
        </p>
      </header>

      <div class="discover-layout">
        <aside class="discover-sidebar">
          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.budget') }}</h3>
            <div class="slider-wrap">
              <input
                v-model.number="budgetSlider"
                class="range-input"
                type="range"
                :min="BUDGET_MIN"
                :max="BUDGET_MAX"
                step="500"
              />
              <div class="slider-labels">
                <span>R$ {{ formatCurrency(BUDGET_MIN) }}</span>
                <span>R$ {{ formatCurrency(BUDGET_MAX) }}+</span>
              </div>
              <p class="slider-hint">
                {{ $t('discover.filters.upTo') }} <strong>R$ {{ formatCurrency(effectiveMaxBudget) }}</strong> {{ $t('discover.filters.perMonth') }}
                <span v-if="budgetSlider >= BUDGET_MAX" class="muted">{{ $t('discover.filters.noLimit') }}</span>
              </p>
            </div>
          </section>

          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.region') }}</h3>
            <div class="chip-row">
              <button
                v-for="chip in regionChips"
                :key="chip.value || 'global'"
                type="button"
                class="chip"
                :class="{ 'chip-active': filters.region === chip.value }"
                @click="setRegion(chip.value)"
              >
                {{ chip.label }}
              </button>
            </div>
          </section>

          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.country') }}</h3>
            <CountrySearchField
              v-model="filters.countryCode"
              label=""
              :placeholder="$t('discover.filters.countryPlaceholder')"
              class="discover-country-field"
            />
          </section>

          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.city') }}</h3>
            <input v-model.trim="filters.city" type="text" class="text-input" :placeholder="$t('discover.filters.cityPlaceholder')" />
          </section>

          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.internet') }}</h3>
            <div class="segment-grid">
              <button
                v-for="opt in internetOptions"
                :key="opt.id"
                type="button"
                class="segment"
                :class="{ 'segment-active': internetTier === opt.id }"
                @click="internetTier = internetTier === opt.id ? '' : opt.id"
              >
                {{ $t(opt.labelKey) }}
              </button>
            </div>
          </section>

          <section class="filter-block">
            <div class="filter-heading-row">
              <h3 class="filter-heading tight">{{ $t('discover.filters.safety') }}</h3>
              <span v-if="minScore >= 8" class="badge-green">{{ $t('discover.filters.safetyScore', { score: minScore.toFixed(1) }) }}</span>
              <span v-else class="badge-soft">{{ $t('discover.filters.safetyScoreMin', { score: minScore.toFixed(1) }) }}</span>
            </div>
            <input
              v-model.number="minScore"
              class="range-input range-secondary"
              type="range"
              min="1"
              max="10"
              step="0.5"
            />
          </section>

          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.ambience') }}</h3>
            <div class="ambience-list">
              <button
                type="button"
                class="ambience-btn"
                :class="{ active: ambience === 'urban' }"
                @click="toggleAmbience('urban')"
              >
                <span class="ambience-inner">
                  <span class="material-symbols-outlined ico">location_city</span>
                  {{ $t('discover.filters.ambienceUrban') }}
                </span>
                <span class="material-symbols-outlined chev">chevron_right</span>
              </button>
              <button
                type="button"
                class="ambience-btn"
                :class="{ active: ambience === 'beach' }"
                @click="toggleAmbience('beach')"
              >
                <span class="ambience-inner">
                  <span class="material-symbols-outlined ico ico-sea">beach_access</span>
                  {{ $t('discover.filters.ambienceBeach') }}
                </span>
                <span class="material-symbols-outlined chev">chevron_right</span>
              </button>
              <button
                type="button"
                class="ambience-btn"
                :class="{ active: ambience === 'nature' }"
                @click="toggleAmbience('nature')"
              >
                <span class="ambience-inner">
                  <span class="material-symbols-outlined ico ico-nature">forest</span>
                  {{ $t('discover.filters.ambienceNature') }}
                </span>
                <span class="material-symbols-outlined chev">chevron_right</span>
              </button>
            </div>
          </section>

          <section class="filter-block">
            <h3 class="filter-heading">{{ $t('discover.filters.style') }}</h3>
            <div class="tag-pills">
              <button
                v-for="t in types"
                :key="t"
                type="button"
                class="mini-pill"
                :class="{ on: filters.type === t }"
                @click="filters.type = filters.type === t ? '' : t"
              >
                {{ t }}
              </button>
            </div>
          </section>

          <div class="sidebar-actions">
            <button type="button" class="btn-clear-sidebar" @click="clearFilters">{{ $t('discover.filters.clearFilters') }}</button>
          </div>
        </aside>

        <div class="discover-results">
          <div class="results-toolbar">
            <p class="results-count">
              <span v-if="loading" class="sk sk-results-bar" aria-hidden="true" />
              <template v-else>
                <strong>{{ sortedCards.length }}</strong> {{ sortedCards.length !== 1 ? $t('discover.results.destinationPlural') : $t('discover.results.destination') }}
                <span v-if="totalPages > 1" class="muted">
                  {{ $t('discover.results.page', { page, total: totalPages }) }}
                </span>
              </template>
            </p>
            <label class="sort-wrap">
              <span class="sort-label">{{ $t('discover.results.sort') }}</span>
              <select v-model="sortBy" class="select-input sort-select">
                <option value="relevance">{{ $t('discover.results.sortRelevance') }}</option>
                <option value="costAsc">{{ $t('discover.results.sortCostAsc') }}</option>
                <option value="costDesc">{{ $t('discover.results.sortCostDesc') }}</option>
                <option value="name">{{ $t('discover.results.sortName') }}</option>
              </select>
            </label>
          </div>

          <p v-if="!loading && !sortedCards.length" class="empty-panel">
            {{ $t('discover.results.empty') }}
          </p>

          <DiscoverGridSkeleton
            v-else-if="loading"
            class="discover-skel-grid"
            :count="pageSize"
            :columns="3"
          />

          <div v-else class="card-grid">
            <article
              v-for="card in paginatedCards"
              :key="card.id"
              class="city-card"
            >
              <div class="city-card-media" @click="goToCity(card.id)">
                <img :src="card.imageUrl" :alt="card.name" />
                <div class="score-pill">
                  <span class="material-symbols-outlined star">star</span>
                  {{ Number(card.scoreOverall || 0).toFixed(1) }}
                </div>
              </div>
              <div class="city-card-body">
                <div class="city-card-top">
                  <div>
                    <h2 class="city-name">{{ card.name }}</h2>
                    <p class="country-line">{{ card.country }}</p>
                  </div>
                  <div class="cost-block">
                    <span class="cost-label">{{ $t('discover.results.avgCost') }}</span>
                    <span class="cost-value">R$ {{ formatCurrency(card.monthlyCost) }}</span>
                  </div>
                </div>
                <div class="tag-row">
                  <span
                    v-for="tag in displayTags(card)"
                    :key="`${card.id}-${tag.key}`"
                    class="dest-tag"
                    :class="tag.tone"
                  >
                    {{ tag.text }}
                  </span>
                </div>
                <button type="button" class="btn-details" @click="goToCity(card.id)">{{ $t('discover.results.details') }}</button>
              </div>
            </article>
          </div>

          <nav v-if="totalPages > 1" class="pagination" :aria-label="$t('discover.pagination.ariaLabel')">
            <button
              type="button"
              class="page-btn"
              :disabled="page <= 1"
              :aria-label="$t('discover.pagination.prev')"
              @click="page = Math.max(1, page - 1)"
            >
              <span class="material-symbols-outlined">chevron_left</span>
            </button>
            <div class="page-numbers">
              <button
                v-for="p in pageButtons"
                :key="p.key"
                type="button"
                class="page-num"
                :class="{ current: p.n === page, ellipsis: p.ellipsis }"
                :disabled="p.ellipsis"
                @click="!p.ellipsis && (page = p.n)"
              >
                {{ p.label }}
              </button>
            </div>
            <button
              type="button"
              class="page-btn"
              :disabled="page >= totalPages"
              :aria-label="$t('discover.pagination.next')"
              @click="page = Math.min(totalPages, page + 1)"
            >
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </nav>
        </div>
      </div>
    </main>

    <footer class="discover-footer">
      <div class="footer-inner">
        <span class="footer-brand">{{ $t('discover.footer.brand') }}</span>
        <div class="footer-links">
          <router-link to="/discover">{{ $t('discover.footer.cities') }}</router-link>
          <router-link to="/privacy">{{ $t('app.footer.privacy') }}</router-link>
          <router-link to="/terms">{{ $t('app.footer.terms') }}</router-link>
        </div>
        <p class="footer-copy">{{ $t('discover.footer.copyright', { year }) }}</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import axios from "axios";
import AppTopHeader from "../components/AppTopHeader.vue";
import DiscoverGridSkeleton from "../components/skeleton/DiscoverGridSkeleton.vue";
import CountrySearchField from "../components/CountrySearchField.vue";
import { flipmemoApiBase } from "@/config/flipmemoApi";

const BUDGET_MIN = 2000;
const BUDGET_MAX = 25000;

export default {
  name: "DiscoverPage",
  components: { AppTopHeader, DiscoverGridSkeleton, CountrySearchField },
  setup() {
    const { t, locale } = useI18n();
    useHead({
      title: computed(() => t('seo.discover.title')),
      meta: [
        { name: 'description', content: computed(() => t('seo.discover.description')) },
        { property: 'og:title', content: computed(() => t('seo.discover.title')) },
        { property: 'og:description', content: computed(() => t('seo.discover.description')) },
        { property: 'og:image', content: '/logo.svg' },
        { property: 'og:locale', content: computed(() => locale.value === 'pt-BR' ? 'pt_BR' : 'en_US') },
        { property: 'og:url', content: 'https://nomade-digital.com/discover' },
        { property: 'og:type', content: 'website' },
      ],
      link: [
        { rel: 'canonical', href: 'https://nomade-digital.com/discover' },
      ],
    });
  },
  data() {
    return {
      BUDGET_MIN,
      BUDGET_MAX,
      loading: false,
      rawCards: [],
      allowPersist: false,
      skipPersist: false,
      persistTimer: null,
      budgetSlider: BUDGET_MAX,
      minScore: 1,
      internetTier: "",
      ambience: "",
      sortBy: "relevance",
      page: 1,
      pageSize: 9,
      filters: {
        maxCost: null,
        region: "",
        countryCode: "",
        city: "",
        type: "",
      },
      regionOptions: [],
      types: ["Cheap", "Beach", "Fast internet", "City"],
      internetOptions: [
        { id: "poor", labelKey: "discover.filters.internetPoor" },
        { id: "good", labelKey: "discover.filters.internetGood" },
        { id: "excellent", labelKey: "discover.filters.internetExcellent" },
        { id: "gigabit", labelKey: "discover.filters.internetGigabit" },
      ],
      regionPresets: [
        { label: "Global", value: "" },
        { label: "Europa", value: "Europe" },
        { label: "Ásia", value: "Asia" },
        { label: "Américas", value: "Americas" },
        { label: "África", value: "Africa" },
        { label: "Oceania", value: "Oceania" },
      ],
    };
  },
  computed: {
    year() {
      return new Date().getFullYear();
    },
    effectiveMaxBudget() {
      if (this.budgetSlider >= BUDGET_MAX) return BUDGET_MAX;
      return this.budgetSlider;
    },
    regionChips() {
      const extra = (this.regionOptions || [])
        .filter((r) => !this.regionPresets.some((p) => p.value === r))
        .map((r) => ({ label: r, value: r }));
      return [...this.regionPresets, ...extra];
    },
    filteredByInternet() {
      return (this.rawCards || []).filter((c) => this.passesInternet(c, this.internetTier));
    },
    sortedCards() {
      const list = [...this.filteredByInternet];
      if (this.sortBy === "costAsc") {
        list.sort((a, b) => Number(a.monthlyCost || 0) - Number(b.monthlyCost || 0));
      } else if (this.sortBy === "costDesc") {
        list.sort((a, b) => Number(b.monthlyCost || 0) - Number(a.monthlyCost || 0));
      } else if (this.sortBy === "name") {
        list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pt"));
      } else {
        list.sort((a, b) => Number(b.scoreOverall || 0) - Number(a.scoreOverall || 0));
      }
      return list;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedCards.length / this.pageSize));
    },
    paginatedCards() {
      const start = (this.page - 1) * this.pageSize;
      return this.sortedCards.slice(start, start + this.pageSize);
    },
    pageButtons() {
      const t = this.totalPages;
      const c = this.page;
      if (t <= 1) return [];
      const nums = new Set([1, t, c, c - 1, c + 1].filter((n) => n >= 1 && n <= t));
      const sorted = [...nums].sort((a, b) => a - b);
      const out = [];
      sorted.forEach((n, idx) => {
        if (idx > 0 && n - sorted[idx - 1] > 1) {
          out.push({ key: `e-${n}-${idx}`, n: 0, label: "…", ellipsis: true });
        }
        out.push({ key: `p-${n}`, n, label: String(n), ellipsis: false });
      });
      return out;
    },
  },
  watch: {
    sortedCards() {
      if (this.page > this.totalPages) this.page = this.totalPages;
    },
    "$route.query": {
      deep: true,
      immediate: true,
      handler() {
        this.syncFiltersFromQuery();
        this.fetchDestinations();
      },
    },
    filters: {
      deep: true,
      handler() {
        if (!this.allowPersist || this.skipPersist) return;
        this.schedulePersistFilters(true);
      },
    },
    budgetSlider() {
      if (!this.allowPersist || this.skipPersist) return;
      this.schedulePersistFilters(true);
    },
    minScore() {
      if (!this.allowPersist || this.skipPersist) return;
      this.schedulePersistFilters(true);
    },
    internetTier() {
      if (!this.allowPersist || this.skipPersist) return;
      this.schedulePersistFilters(true);
    },
    ambience() {
      if (!this.allowPersist || this.skipPersist) return;
      this.schedulePersistFilters(true);
    },
    sortBy() {
      if (!this.allowPersist || this.skipPersist) return;
      this.persistFiltersToRoute(false);
    },
  },
  mounted() {
    this.loadRegionFacetOptions();
    this.$nextTick(() => {
      this.allowPersist = true;
    });
  },
  beforeUnmount() {
    if (this.persistTimer) clearTimeout(this.persistTimer);
  },
  methods: {
    apiBase() {
      return flipmemoApiBase;
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR");
    },
    goToCity(id) {
      this.$router.push(`/city/${id}`);
    },
    passesInternet(card, tier) {
      if (!tier) return true;
      const si = Number(card.scoreInternet ?? card.score_internet ?? 0);
      const tags = card.tags || [];
      const hasFast = tags.some((t) => /fast|gigabit|rápida|rápido/i.test(String(t)));
      if (tier === "poor") {
        if (!si) return true;
        return si < 5;
      }
      if (tier === "good") return si >= 5 && si < 7.5;
      if (tier === "excellent") return si >= 7.5 && si < 9;
      if (tier === "gigabit") return si >= 9 || hasFast;
      return true;
    },
    displayTags(card) {
      const tags = (card.tags || []).slice(0, 4);
      return tags.map((t) => {
        const s = String(t);
        let tone = "";
        if (/cheap|barato/i.test(s)) tone = "tone-green";
        else if (/beach|praia|sea/i.test(s)) tone = "tone-earth";
        else if (/fast|internet|gigabit/i.test(s)) tone = "tone-blue";
        return { key: s, text: s.toUpperCase(), tone };
      });
    },
    setRegion(value) {
      this.filters.region = value;
    },
    queryVal(v) {
      if (v == null) return "";
      return Array.isArray(v) ? v.join(",") : String(v);
    },
    queriesEqual(a, b) {
      const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
      for (const k of keys) {
        if (this.queryVal((a || {})[k]) !== this.queryVal((b || {})[k])) return false;
      }
      return true;
    },
    schedulePersistFilters(resetPage) {
      if (this.persistTimer) clearTimeout(this.persistTimer);
      this.persistTimer = setTimeout(() => {
        this.persistTimer = null;
        this.persistFiltersToRoute(resetPage);
      }, 300);
    },
    persistFiltersToRoute(resetPage) {
      if (!this.allowPersist || this.skipPersist) return;
      if (resetPage) this.page = 1;
      const next = this.buildQueryFromFilters();
      const cur = this.$route.query || {};
      if (this.queriesEqual(next, cur)) return;
      this.$router.replace({ path: "/discover", query: next }).catch(() => {});
    },
    toggleAmbience(key) {
      if (this.ambience === key) {
        this.ambience = "";
        if (key === "urban" || key === "beach") this.filters.type = "";
        return;
      }
      this.ambience = key;
      if (key === "urban") this.filters.type = "City";
      else if (key === "beach") this.filters.type = "Beach";
      else this.filters.type = "";
    },
    syncFiltersFromQuery() {
      this.skipPersist = true;
      const query = this.$route.query || {};
      const mcRaw = query.maxCost;
      const mc = mcRaw != null && mcRaw !== "" ? Number(mcRaw) : NaN;
      if (Number.isFinite(mc) && mc > 0) {
        this.filters.maxCost = mc;
        this.budgetSlider = Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, mc));
      } else {
        this.filters.maxCost = null;
        this.budgetSlider = BUDGET_MAX;
      }
      this.filters.region = query.region != null ? String(query.region) : "";
      this.filters.countryCode =
        query.countryCode != null && String(query.countryCode).trim() !== ""
          ? String(query.countryCode).trim().toUpperCase()
          : "";
      this.filters.city = query.city != null ? String(query.city).trim() : "";
      this.filters.type = query.type != null ? String(query.type) : "";
      const ms = query.minScore != null && query.minScore !== "" ? Number(query.minScore) : NaN;
      this.minScore = Number.isFinite(ms) && ms >= 1 ? ms : 1;
      this.sortBy = query.sort != null && String(query.sort) ? String(query.sort) : "relevance";
      this.internetTier = query.internetTier != null ? String(query.internetTier) : "";
      let amb = query.ambience != null ? String(query.ambience) : "";
      if (!["urban", "beach", "nature"].includes(amb)) {
        if (this.filters.type === "City") amb = "urban";
        else if (this.filters.type === "Beach") amb = "beach";
        else amb = "";
      }
      this.ambience = amb;
      if (this.ambience === "urban" && this.filters.type !== "City") this.filters.type = "City";
      if (this.ambience === "beach" && this.filters.type !== "Beach") this.filters.type = "Beach";
      if (this.ambience === "nature") this.filters.type = "";
      this.$nextTick(() => {
        this.skipPersist = false;
      });
    },
    buildQueryFromFilters() {
      const query = {};
      if (this.budgetSlider < BUDGET_MAX) query.maxCost = String(this.budgetSlider);
      if (this.filters.region) query.region = this.filters.region;
      if (this.filters.countryCode) query.countryCode = String(this.filters.countryCode).trim().toUpperCase();
      if (this.filters.city) query.city = this.filters.city;
      if (this.filters.type) query.type = this.filters.type;
      if (this.minScore > 1) query.minScore = String(this.minScore);
      if (this.sortBy && this.sortBy !== "relevance") query.sort = this.sortBy;
      if (this.internetTier) query.internetTier = this.internetTier;
      if (this.ambience) query.ambience = this.ambience;
      return query;
    },
    clearFilters() {
      this.page = 1;
      this.allowPersist = false;
      this.$router.replace({ path: "/discover" }).then(() => {
        this.$nextTick(() => {
          this.allowPersist = true;
        });
      }).catch(() => {
        this.allowPersist = true;
      });
    },
    async loadRegionFacetOptions() {
      try {
        const { data } = await axios.get(`${this.apiBase()}/destinations`);
        const set = new Set((data || []).map((c) => c.region).filter(Boolean));
        this.regionOptions = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), "pt"));
      } catch {
        this.regionOptions = [];
      }
    },
    async fetchDestinations() {
      this.loading = true;
      try {
        const params = {};
        if (this.budgetSlider < BUDGET_MAX) params.maxCost = String(this.budgetSlider);
        if (this.filters.region) params.region = this.filters.region;
        if (this.filters.countryCode) params.countryCode = String(this.filters.countryCode).trim().toUpperCase();
        if (this.filters.city) params.city = this.filters.city;
        if (this.filters.type) params.type = this.filters.type;
        if (this.minScore > 1) params.minScore = String(this.minScore);
        const response = await axios.get(`${this.apiBase()}/destinations`, { params });
        this.rawCards = response.data || [];
      } catch {
        this.rawCards = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.discover-page {
  --color-primary: #4f8ef7;
  --color-primary-container: #1e3a8a;
  --color-on-primary: #ffffff;
  --color-surface: #0d1b2e;
  --color-surface-low: #081526;
  --color-surface-high: #1a2e48;
  --color-card: #152338;
  --color-on-surface: #e2eaf4;
  --color-on-variant: #94adc8;
  --color-secondary: #34d399;
  --color-tertiary: #fb923c;
  --shadow-card: 0 12px 32px -4px rgba(25, 28, 30, 0.06);
  --radius-lg: 12px;
  --radius-xl: 16px;
  --radius-full: 9999px;
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--color-surface);
  color: var(--color-on-surface);
  font-family: Inter, "Segoe UI", system-ui, sans-serif;
}

.discover-main {
  max-width: 1600px;
  margin: 0 auto;
  padding: 96px 24px 48px;
}

@media (min-width: 768px) {
  .discover-main {
    padding-left: 48px;
    padding-right: 48px;
  }
}

.discover-hero {
  margin-bottom: 48px;
}

.discover-title {
  margin: 0 0 8px;
  font-size: clamp(1.125rem, 2vw, 1.5rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.discover-lead {
  margin: 0;
  font-size: 1.125rem;
  color: var(--color-on-variant);
  max-width: 36rem;
  line-height: 1.5;
}

.discover-layout {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (min-width: 1024px) {
  .discover-layout {
    flex-direction: row;
    align-items: flex-start;
    gap: 48px;
  }
  .discover-sidebar {
    width: 26%;
    min-width: 260px;
    max-width: 320px;
    flex-shrink: 0;
  }
  .discover-results {
    flex: 1;
    min-width: 0;
  }
}

.discover-sidebar {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.filter-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-heading {
  margin: 0;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--color-on-variant);
}

.filter-heading-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.filter-heading.tight {
  margin: 0;
}

.badge-green {
  font-size: 11px;
  font-weight: 800;
  color: var(--color-secondary);
}

.badge-soft {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-on-variant);
}

.slider-wrap {
  padding: 0 4px;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-on-surface);
}

.slider-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--color-on-variant);
}

.slider-hint .muted,
.muted {
  color: #94a3b8;
  font-weight: 500;
}

.range-input {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--color-surface-high);
  appearance: none;
  cursor: pointer;
}

.range-input::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-primary);
  margin-top: -6px;
  box-shadow: 0 2px 8px rgba(0, 74, 198, 0.35);
}

.range-input::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 3px;
  background: var(--color-surface-high);
}

.range-input.range-secondary::-webkit-slider-thumb {
  background: var(--color-secondary);
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  border: none;
  padding: 8px 16px;
  border-radius: var(--radius-full);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: var(--color-surface-high);
  color: var(--color-on-variant);
  transition: background 0.15s ease, color 0.15s ease;
}

.chip:hover {
  background: #dce0e4;
}

.chip-active {
  background: var(--color-primary-container);
  color: var(--color-on-primary);
}

.text-input,
.select-input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  background: var(--color-surface-low);
  border-radius: var(--radius-lg);
  padding: 14px 16px;
  font-size: 14px;
  color: var(--color-on-surface);
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.text-input:focus,
.select-input:focus {
  outline: none;
  background: var(--color-card);
  box-shadow: 0 0 0 1px rgba(0, 74, 198, 0.35);
}

.select-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.segment-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.segment {
  border: 1px solid rgba(195, 198, 215, 0.45);
  padding: 12px;
  border-radius: var(--radius-lg);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--color-on-surface);
  transition: border-color 0.15s, background 0.15s;
}

.segment:hover {
  background: var(--color-surface-low);
}

.segment-active {
  border-width: 2px;
  border-color: var(--color-primary);
  background: rgba(37, 99, 235, 0.08);
  color: var(--color-primary);
  font-weight: 700;
}

.ambience-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ambience-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: none;
  border-radius: var(--radius-lg);
  background: var(--color-card);
  box-shadow: var(--shadow-card);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-on-surface);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ambience-btn:hover {
  box-shadow: 0 14px 36px -6px rgba(25, 28, 30, 0.1);
}

.ambience-btn.active {
  box-shadow: 0 0 0 2px rgba(0, 74, 198, 0.25);
}

.ambience-inner {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ambience-btn .ico {
  font-size: 22px;
  color: var(--color-primary);
}

.ambience-btn .ico-sea {
  color: var(--color-secondary);
}

.ambience-btn .ico-nature {
  color: var(--color-tertiary);
}

.ambience-btn .chev {
  font-size: 18px;
  color: #94a3b8;
}

.tag-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mini-pill {
  border: none;
  padding: 6px 12px;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: var(--color-surface-high);
  color: var(--color-on-variant);
}

.mini-pill.on {
  background: rgba(0, 74, 198, 0.12);
  color: var(--color-primary);
}

.sidebar-actions {
  padding-top: 8px;
}

.btn-clear-sidebar {
  width: 100%;
  border: none;
  border-radius: var(--radius-lg);
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: var(--color-surface-low);
  color: var(--color-on-variant);
  transition: background 0.15s ease, color 0.15s ease;
}

.btn-clear-sidebar:hover {
  background: var(--color-surface-high);
  color: var(--color-on-surface);
}

.results-toolbar {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}

@media (min-width: 640px) {
  .results-toolbar {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.results-count {
  margin: 0;
  font-size: 15px;
  color: var(--color-on-variant);
}

.results-count strong {
  color: var(--color-on-surface);
}

.sk-results-bar {
  display: inline-block;
  height: 18px;
  width: min(220px, 70%);
  vertical-align: middle;
}

.sort-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sort-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-primary);
}

.sort-select {
  width: auto;
  min-width: 160px;
  padding: 10px 14px;
}

.empty-panel {
  padding: 32px;
  text-align: center;
  color: var(--color-on-variant);
  background: var(--color-card);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
}

.card-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
}

@media (min-width: 768px) {
  .card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .card-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.city-card {
  background: var(--color-card);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.city-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 48px -8px rgba(25, 28, 30, 0.12);
}

.city-card-media {
  position: relative;
  height: 240px;
  cursor: pointer;
  overflow: hidden;
}

.city-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.45s ease;
}

.city-card:hover .city-card-media img {
  transform: scale(1.06);
}

.score-pill {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  font-size: 14px;
  font-weight: 800;
  color: var(--color-primary);
}

.score-pill .star {
  font-size: 16px;
  font-variation-settings: "FILL" 1;
}

.city-card-body {
  padding: 24px;
}

.city-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.city-name {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.country-line {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-on-variant);
}

.cost-block {
  text-align: right;
}

.cost-label {
  display: block;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-on-variant);
}

.cost-value {
  font-size: 1.25rem;
  font-weight: 900;
  color: var(--color-primary);
  letter-spacing: -0.02em;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.dest-tag {
  padding: 6px 12px;
  border-radius: var(--radius-full);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  background: var(--color-surface-high);
  color: var(--color-on-variant);
}

.dest-tag.tone-green {
  background: rgba(0, 108, 73, 0.15);
  color: var(--color-secondary);
}

.dest-tag.tone-earth {
  background: rgba(148, 55, 0, 0.12);
  color: var(--color-tertiary);
}

.dest-tag.tone-blue {
  background: rgba(37, 99, 235, 0.12);
  color: var(--color-primary);
}

.btn-details {
  width: 100%;
  border: none;
  border-radius: var(--radius-lg);
  padding: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  background: var(--color-primary-container);
  color: var(--color-on-primary);
  transition: background 0.15s, transform 0.1s;
}

.btn-details:hover {
  background: var(--color-primary);
}

.btn-details:active {
  transform: scale(0.98);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 48px;
}

.page-btn {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-high);
  color: var(--color-on-variant);
  cursor: pointer;
  transition: background 0.15s;
}

.page-btn:hover:not(:disabled) {
  background: #dce0e4;
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.page-num {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  background: transparent;
  color: var(--color-on-surface);
}

.page-num:hover:not(:disabled):not(.ellipsis) {
  background: var(--color-surface-low);
}

.page-num.current {
  background: var(--color-primary);
  color: var(--color-on-primary);
  font-weight: 800;
}

.page-num.ellipsis {
  cursor: default;
  color: var(--color-on-variant);
}

.discover-footer {
  margin-top: 80px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  background: #f1f5f9;
  padding: 48px 24px;
}

.footer-inner {
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

@media (min-width: 768px) {
  .footer-inner {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    text-align: left;
  }
}

.footer-brand {
  font-size: 1.125rem;
  font-weight: 900;
  color: var(--color-on-surface);
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
}

.footer-links a {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
}

.footer-links a:hover {
  color: var(--color-primary);
}

.footer-copy {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  width: 100%;
}

@media (min-width: 768px) {
  .footer-copy {
    width: auto;
    margin-left: auto;
  }
}

:deep(.discover-country-field .inp) {
  background: var(--color-surface-low);
  border: none;
  border-radius: var(--radius-lg);
}

:deep(.discover-country-field .inp:focus) {
  box-shadow: 0 0 0 1px rgba(0, 74, 198, 0.35);
}
</style>
